/**
 * What each Stat group holds, shown before somebody picks one.
 *
 * A Stat group (stored as a profile's `scope`) is a top-level block a source
 * publishes the same statistics under — a season's totals beside its per-game
 * figures, say. The names alone rarely say which is which, so each group is
 * listed with the fields it carries and a few of the values read from them.
 * Nothing here is fetched: it is the discovery result the screen already has.
 */

import type { DiscoveredField } from "../api";
import { Badge, Button, Code, Modal, Muted } from "./ui";

/** How many sample values a field shows. Enough to read a unit, no more. */
const SAMPLES_SHOWN = 3;

export function StatGroupPreview({
  open,
  onClose,
  groups,
  fields,
  chosen,
  onChoose,
}: {
  open: boolean;
  onClose: () => void;
  groups: readonly string[];
  /** Every field across every group, as the unscoped read found them. */
  fields: readonly DiscoveredField[];
  /** The group in use, or "" for all of them. */
  chosen: string;
  onChoose: (group: string) => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="What each Stat group contains"
      description="The fields this source publishes under each group, with values read from it. Picking a group reads every statistic from that group alone."
      width="max-w-[52rem]"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-lg">
        {groups.map((group) => {
          const members = fields.filter((field) => field.block === group);
          return (
            <section key={group} className="flex flex-col gap-sm">
              <div className="flex flex-wrap items-center gap-xs">
                <Code>{group}</Code>
                <Badge tone="accent">
                  {members.length} field{members.length === 1 ? "" : "s"}
                </Badge>
                {chosen === group && <Badge tone="success">In use</Badge>}
                <div className="ml-auto">
                  <Button
                    size="sm"
                    variant={chosen === group ? "ghost" : "secondary"}
                    disabled={chosen === group}
                    onClick={() => onChoose(group)}
                  >
                    Use this group
                  </Button>
                </div>
              </div>
              {members.length === 0 ? (
                <Muted>No fields were read under this group.</Muted>
              ) : (
                <table className="table table-sticky-head-panel">
                  {/* Sticky against the dialog body, which is the scroller here
                      rather than the page — a group of ninety fields is several
                      screens inside an 85vh panel, and the two headings are what
                      say which column is the path and which the values. */}
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Sample values</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((field) => (
                      <tr key={field.path}>
                        <td>
                          <Code className="code-sm">{withinGroup(field.path, group)}</Code>
                          {field.looks_numeric && (
                            <span className="ml-xs">
                              <Badge tone="neutral">number</Badge>
                            </span>
                          )}
                        </td>
                        <td>
                          <Muted>
                            {field.samples.length === 0
                              ? "—"
                              : field.samples.slice(0, SAMPLES_SHOWN).map(String).join(", ")}
                          </Muted>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          );
        })}
      </div>
    </Modal>
  );
}

/** The path as it reads inside its group, without the group's own name. */
function withinGroup(path: string, group: string): string {
  return path.startsWith(`${group}.`) ? path.slice(group.length + 1) : path;
}
